import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslations } from '../../hooks/useTranslations';

const YearOverYearChart = ({ data = [] }) => {
    const [selectedPeriod, setSelectedPeriod] = useState('3y');

    const t = useTranslations()
    
    // Time period options
    const timePeriods = [
        { key: '1y', label: '1Y', years: 1 },
        { key: '2y', label: '2Y', years: 2 },
        { key: '3y', label: '3Y', years: 3 },
        { key: '5y', label: '5Y', years: 5 },
        { key: 'max', label: t.charts.periods.max, years: null }
    ];

    // Color palette for different years
    const yearColors = [
        '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6', 
        '#F97316', '#06B6D4', '#84CC16', '#EC4899', '#6B7280'
    ];

    // Sample data
    const sampleData = data.length === 0 ? [
        { date: '2022-01-01', value: 32 }, { date: '2022-02-01', value: 28 }, { date: '2022-03-01', value: 35 },
        { date: '2022-04-01', value: 42 }, { date: '2022-05-01', value: 38 }, { date: '2022-06-01', value: 45 },
        { date: '2022-07-01', value: 48 }, { date: '2022-08-01', value: 41 }, { date: '2022-09-01', value: 52 },
        { date: '2022-10-01', value: 46 }, { date: '2022-11-01', value: 58 }, { date: '2022-12-01', value: 55 },
        { date: '2023-01-01', value: 45 }, { date: '2023-02-01', value: 52 }, { date: '2023-03-01', value: 48 },
        { date: '2023-04-01', value: 61 }, { date: '2023-05-01', value: 55 }, { date: '2023-06-01', value: 67 },
        { date: '2023-07-01', value: 71 }, { date: '2023-08-01', value: 58 }, { date: '2023-09-01', value: 76 },
        { date: '2023-10-01', value: 69 }, { date: '2023-11-01', value: 83 }, { date: '2023-12-01', value: 78 },
        { date: '2024-01-01', value: 82 }, { date: '2024-02-01', value: 89 }, { date: '2024-03-01', value: 95 },
        { date: '2024-04-01', value: 101 }, { date: '2024-05-01', value: 97 }, { date: '2024-06-01', value: 108 },
        { date: '2024-07-01', value: 112 }, { date: '2024-08-01', value: 105 }, { date: '2024-09-01', value: 118 },
        { date: '2024-10-01', value: 125 }, { date: '2024-11-01', value: 131 }, { date: '2024-12-01', value: 128 }
    ] : data;

    // Process data for chart
    const processedData = useMemo(() => {
        const dataToProcess = (!data || !Array.isArray(data) || data.length === 0) ? sampleData : data;

        // Group data by month and year
        const groupedData = {};
        const availableYears = new Set();

        dataToProcess.forEach(item => {
            const date = new Date(item.date);
            const year = date.getFullYear();
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const monthKey = String(date.getMonth() + 1).padStart(2, '0');

            availableYears.add(year);

            if (!groupedData[monthKey]) {
                groupedData[monthKey] = { month, monthKey };
            }
            groupedData[monthKey][year] = item.value;
        });

        // Filter years based on selected period
        const sortedYears = Array.from(availableYears).sort((a, b) => b - a);
        const currentYear = new Date().getFullYear();

        let yearsToShow;
        if (selectedPeriod === 'max') {
            yearsToShow = sortedYears;
        } else {
            const period = timePeriods.find(p => p.key === selectedPeriod);
            const cutoffYear = currentYear - period.years;
            yearsToShow = sortedYears.filter(year => year > cutoffYear);
        }

        const result = Object.values(groupedData)
            .map(monthData => {
                const filteredMonthData = { month: monthData.month, monthKey: monthData.monthKey };
                yearsToShow.forEach(year => {
                    if (monthData[year] !== undefined) {
                        filteredMonthData[year] = monthData[year];
                    }
                });
                return filteredMonthData;
            })
            .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

        return { chartData: result, years: yearsToShow };
    }, [sampleData, selectedPeriod]);

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-2 sm:p-3 border border-gray-200 rounded-lg shadow-lg text-xs sm:text-sm">
                    <p className="text-gray-800 font-semibold mb-1 sm:mb-2">{label}</p>
                    {payload
                        .sort((a, b) => b.dataKey - a.dataKey)
                        .map((entry, index) => (
                            <p key={index} className="text-xs sm:text-sm" style={{ color: entry.color }}>
                                {entry.dataKey}: {entry.value} {t.charts.appointments}
                            </p>
                        ))}
                </div>
            );
        }
        return null;
    };

    // Responsive legend
    const ReversedLegend = (props) => {
        const { payload } = props;
        const isMobile = window.innerWidth < 640;
        
        return (
            <div className={`flex ${isMobile ? 'flex-wrap gap-2' : 'gap-4'} justify-center items-center`}>
                {[...payload].reverse().map((entry, index) => (
                    <div key={index} className="flex items-center space-x-1 sm:space-x-2">
                        <div
                            style={{ backgroundColor: entry.color }}
                            className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} rounded-sm`}
                        />
                        <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-700 font-medium`}>
                            {entry.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    // Calculate statistics
    const statistics = useMemo(() => {
        if (!processedData || !processedData.chartData || processedData.chartData.length === 0 || !processedData.years || processedData.years.length === 0) {
            return { totalsByYear: {}, averageGrowth: 0, bestMonth: 'N/A', worstMonth: 'N/A' };
        }

        const totalsByYear = {};
        const monthlyTotals = {};

        processedData.years.forEach(year => {
            totalsByYear[year] = 0;
        });

        processedData.chartData.forEach(monthData => {
            const monthName = monthData.month;
            monthlyTotals[monthName] = 0;

            processedData.years.forEach(year => {
                if (monthData[year]) {
                    totalsByYear[year] += monthData[year];
                    monthlyTotals[monthName] += monthData[year];
                }
            });
        });

        // Calculate year-over-year growth
        const sortedYears = processedData.years.sort();
        let growthRates = [];
        for (let i = 1; i < sortedYears.length; i++) {
            const currentYear = sortedYears[i];
            const previousYear = sortedYears[i - 1];
            if (totalsByYear[previousYear] > 0) {
                const growth = ((totalsByYear[currentYear] - totalsByYear[previousYear]) / totalsByYear[previousYear]) * 100;
                growthRates.push(growth);
            }
        }
        const averageGrowth = growthRates.length > 0 ? growthRates.reduce((sum, rate) => sum + rate, 0) / growthRates.length : 0;

        // Find best and worst performing months
        const monthEntries = Object.entries(monthlyTotals);
        const bestMonth = monthEntries.reduce((max, current) => current[1] > max[1] ? current : max, ['', 0])[0];

        return { totalsByYear, averageGrowth, bestMonth };
    }, [processedData]);

    // Get responsive settings
    const getResponsiveSettings = () => {
        const isMobile = window.innerWidth < 640;
        const isTablet = window.innerWidth < 1024;
        
        return {
            fontSize: isMobile ? 8 : isTablet ? 10 : 12,
            margin: isMobile ? 
                { top: 10, right: 5, left: 5, bottom: 5 } : 
                isTablet ? 
                { top: 15, right: 15, left: 15, bottom: 5 } :
                { top: 20, right: 30, left: 20, bottom: 5 },
            barCategoryGap: isMobile ? "10%" : "20%"
        };
    };

    const responsiveSettings = getResponsiveSettings();

    return (
        <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-2 sm:p-4 lg:p-6">
            {/* Header */}
            <div className="mb-3 sm:mb-4 lg:mb-6">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 mb-1">
                    {t.charts.yearOverYear.title}
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm">
                    {t.charts.yearOverYear.description}
                </p>
            </div>

            {/* Time Period Selector */}
            <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4 lg:mb-6">
                {timePeriods.map((period) => (
                    <button
                        key={period.key}
                        onClick={() => setSelectedPeriod(period.key)}
                        className={`px-2 py-1 sm:px-3 sm:py-1.5 lg:px-4 lg:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                            selectedPeriod === period.key
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {period.label}
                    </button>
                ))}
            </div>

            {/* Chart */}
            <div className="w-full overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <div className="min-w-[320px] sm:min-w-[480px] lg:min-w-full h-60 sm:h-80 lg:h-96">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={processedData.chartData}
                            margin={responsiveSettings.margin}
                            barCategoryGap={responsiveSettings.barCategoryGap}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.6} />
                            <XAxis
                                dataKey="month"
                                stroke="#6B7280"
                                fontSize={responsiveSettings.fontSize}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: responsiveSettings.fontSize }}
                                tickFormatter={(value) => t.charts.months[value.toLowerCase()] || value}
                            />
                            <YAxis
                                stroke="#6B7280"
                                fontSize={responsiveSettings.fontSize}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: responsiveSettings.fontSize }}
                                width={window.innerWidth < 640 ? 20 : 30}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend content={<ReversedLegend />} />
                            {processedData.years.map((year, index) => (
                                <Bar
                                    key={year}
                                    dataKey={year}
                                    fill={yearColors[index % yearColors.length]}
                                    radius={[2, 2, 0, 0]}
                                    name={`${year}`}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Statistics Summary */}
            <div className="mt-3 sm:mt-4 lg:mt-6 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm sm:text-xl lg:text-2xl font-bold text-gray-800">
                        {statistics.averageGrowth > 0 ? '+' : ''}{statistics.averageGrowth.toFixed(1)}%
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">{t.charts.yearOverYear.avgYoyGrowth}</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm sm:text-xl lg:text-2xl font-bold text-gray-800">
                        {t.charts.months[statistics.bestMonth.toLowerCase()] || 'N/A'}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">{t.charts.yearOverYear.bestMonth}</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm sm:text-xl lg:text-2xl font-bold text-gray-800">
                        {processedData.years.length}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">{t.charts.yearOverYear.yearsCompared}</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm sm:text-xl lg:text-2xl font-bold text-blue-600">
                        {Object.keys(statistics.totalsByYear).length > 0 ?
                            Math.max(...Object.values(statistics.totalsByYear)) : 0}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">{t.charts.yearOverYear.bestYearTotal}</div>
                </div>
            </div>
        </div>
    );
};

export default YearOverYearChart;